import Phaser from 'phaser';
import { Direction, directionalVelocity, frameMs, oppositeDirection, State, StateMachine, wait } from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import * as C from 'tower/constants';
import { Hittable } from 'tower/types';

export default class Boomerang extends Phaser.GameObjects.Sprite {
  stateMachine: StateMachine<BoomerangState>;
  declare body: Phaser.Physics.Arcade.Body;
  collider: Phaser.Physics.Arcade.Collider;
  sound: Phaser.Sound.BaseSound;

  constructor(
    scene: BattleScene,
    x: number,
    y: number,
    public direction: Direction,
    target: 'enemy' | 'player',
    public source: Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Transform,
  ) {
    super(scene, x, y, 'items', 24);

    scene.add.existing(this);
    scene.physics.add.existing(this);
    scene.currentRoom.misc.add(this);

    this.body.setSize(8, 8).setMaxSpeed(C.BOOMERANG_THROW_VELOCITY);

    this.setDepth(C.DEPTHS.Items).play('boomerang');

    this.sound = scene.sound.add('boomerang');
    scene.events.addListener('pause', this.handlePause);
    scene.events.addListener('freeze', this.handlePause);
    scene.events.addListener('resume', this.handleResume);
    scene.events.addListener('unfreeze', this.handleResume);

    const hitObjects = new Set();
    this.collider = scene.physics.add.overlap(
      this,
      target === 'enemy' ? scene.currentRoom.enemies : scene.player,
      (_boomerang, gameObject) => {
        const hittable = gameObject as unknown as Hittable;

        // Can only hit an object once per throw
        if (hitObjects.has(hittable)) {
          return;
        }

        hitObjects.add(hittable);
        hittable.hit({
          weaponType: 'boomerang',
          weaponBody: this.body,
          damage: 1,
        });
        if (this.stateMachine.state !== 'return') {
          this.stateMachine.transition('return');
        }
      },
    );

    this.stateMachine = new StateMachine('throw', {
      throw: new ThrowState(scene, this),
      rebound: new ReboundState(scene, this),
      return: new ReturnState(scene, this),
    });
  }

  handlePause = () => {
    this.sound.pause();
  };

  handleResume = () => {
    this.sound.resume();
  };

  update() {
    this.stateMachine.update();
  }

  destroy(fromScene?: boolean) {
    this.scene.events.removeListener('pause', this.handlePause);
    this.scene.events.removeListener('freeze', this.handlePause);
    this.scene.events.removeListener('resume', this.handleResume);
    this.scene.events.removeListener('unfreeze', this.handleResume);

    super.destroy(fromScene);

    this.sound.stop();
    if (this.collider.world) {
      this.collider.destroy();
    }
  }

  static preload(scene: Phaser.Scene) {
    scene.load.audio('boomerang', 'audio/boomerang.mp3');
  }

  static create(scene: BattleScene) {
    scene.anims.create({
      key: 'boomerang',
      frameRate: 30,
      frames: scene.anims.generateFrameNumbers('items', {
        start: 24,
        end: 31,
      }),
      repeat: -1,
    });
  }
}

class BoomerangState extends State {
  constructor(
    protected scene: BattleScene,
    protected boomerang: Boomerang,
  ) {
    super();
  }
}

class ThrowState extends BoomerangState {
  handleEntered() {
    const { boomerang } = this;
    boomerang.body
      .setVelocity(...directionalVelocity(C.BOOMERANG_THROW_VELOCITY, boomerang.direction))
      .setAcceleration(...directionalVelocity(C.BOOMERANG_THROW_ACC, oppositeDirection(boomerang.direction)));
    boomerang.sound.play({ loop: true });
  }

  update() {
    const { boomerang } = this;
    let reversed = false;
    switch (boomerang.direction) {
      case Direction.Up:
        reversed = boomerang.body.velocity.y >= 0;
        break;
      case Direction.Down:
        reversed = boomerang.body.velocity.y <= 0;
        break;
      case Direction.Left:
        reversed = boomerang.body.velocity.x >= 0;
        break;
      case Direction.Right:
        reversed = boomerang.body.velocity.x <= 0;
        break;
    }

    if (reversed) {
      return this.transition('return');
    }

    // Hitting room walls
    if (!Phaser.Geom.Rectangle.Contains(this.scene.currentRoom.wallRect, boomerang.x, boomerang.y)) {
      return this.transition('rebound');
    }
  }
}

class ReboundState extends BoomerangState {
  async handleEntered() {
    const { boomerang, scene } = this;
    boomerang.setFrame(32).stop();
    boomerang.body.setVelocity(0);

    await wait(scene, frameMs(2));

    return this.transition('return');
  }
}

class ReturnState extends BoomerangState {
  startTime!: number;

  handleEntered() {
    const { scene, boomerang } = this;
    this.startTime = scene.time.now;
    boomerang.play('boomerang', true);
    boomerang.body.setAcceleration(0).setVelocity(0);
  }

  update() {
    const { scene, boomerang } = this;

    const speed = C.BOOMERANG_THROW_VELOCITY * ((scene.time.now - this.startTime) / 400);
    scene.physics.moveToObject(boomerang, boomerang.source, speed);

    if (Phaser.Math.Distance.BetweenPoints(boomerang, boomerang.source) < 8) {
      boomerang.sound.stop();
      boomerang.destroy();
      if (boomerang.source === scene.player) {
        scene.player.catch();
      }
    }
  }
}
